/**
 * Utility functions for converting between different odds formats
 */

export interface OddsFormats {
  decimal: number;
  american: number;
  fractional: string;
  percentage: number;
}

/**
 * Calculate proper fractional odds with reduced fractions
 * @param decimal Decimal odds
 * @returns Fractional odds as string (e.g., "7/2", "1/4", "11/10")
 */
function calculateFractionalOdds(decimal: number): string {
  // Convert decimal to fractional odds
  const profit = decimal - 1;
  
  // Handle special cases
  if (profit === 1) return "1/1"; // Even odds
  if (profit < 1) {
    // Odds-on (negative American odds equivalent)
    // e.g., 1.25 decimal = 1/4 fractional
    const denominator = 1 / profit;
    return reduceFraction(1, Math.round(denominator * 100) / 100);
  } else {
    // Odds-against (positive American odds equivalent)  
    // e.g., 3.5 decimal = 5/2 fractional
    return reduceFraction(Math.round(profit * 100) / 100, 1);
  }
}

/**
 * Reduce a fraction to its simplest form
 * @param numerator 
 * @param denominator 
 * @returns Reduced fraction as string
 */
function reduceFraction(numerator: number, denominator: number): string {
  // Convert to whole numbers for GCD calculation
  const factor = 100;
  let num = Math.round(numerator * factor);
  let den = Math.round(denominator * factor);
  
  // Find GCD and reduce
  const gcd = findGCD(num, den);
  num = num / gcd;
  den = den / gcd;
  
  return `${num}/${den}`;
}

/**
 * Find Greatest Common Divisor using Euclidean algorithm
 */
function findGCD(a: number, b: number): number {
  return b === 0 ? a : findGCD(b, a % b);
}

/**
 * Convert decimal odds to all other formats
 * @param decimal The decimal odds (e.g., 2.50, 1.85)
 * @returns Object containing all odds formats
 */
export function convertFromDecimal(decimal: number): OddsFormats {
  if (decimal <= 1) {
    throw new Error('Decimal odds must be greater than 1');
  }

  // Calculate American odds
  const american = decimal >= 2.0 
    ? Math.round((decimal - 1) * 100)  // Positive odds
    : Math.round(-100 / (decimal - 1)); // Negative odds

  // Calculate implied probability percentage
  const percentage = Math.round((1 / decimal) * 100 * 100) / 100; // Round to 2 decimal places

  // Calculate fractional odds
  const fractional = calculateFractionalOdds(decimal);

  return {
    decimal,
    american,
    fractional,
    percentage
  };
}

/**
 * Format American odds with proper +/- prefix
 * @param american American odds number
 * @returns Formatted string (e.g., "+150", "-200")
 */
export function formatAmericanOdds(american: number): string {
  return american > 0 ? `+${american}` : `${american}`;
}

/**
 * Format odds for display with American and decimal
 * @param decimal Decimal odds
 * @returns Formatted string (e.g., "+150 (2.50)")
 */
export function formatOddsDisplay(decimal?: number): string {
  if (!decimal) return 'N/A';
  
  try {
    const converted = convertFromDecimal(decimal);
    return `${formatAmericanOdds(converted.american)} (${decimal.toFixed(2)})`;
  } catch (error) {
    console.error('Error converting odds:', decimal, error);
    return `${decimal.toFixed(2)} (conversion error)`;
  }
}

/**
 * Format odds for display with percentage
 * @param decimal Decimal odds
 * @returns Formatted string (e.g., "+150 (40%)")
 */
export function formatOddsWithPercentage(decimal?: number): string {
  if (!decimal) return 'N/A';
  
  const converted = convertFromDecimal(decimal);
  return `${formatAmericanOdds(converted.american)} (${converted.percentage}%)`;
}

/**
 * Get implied probability from decimal odds
 * @param decimal Decimal odds
 * @returns Probability as percentage (0-100)
 */
export function getImpliedProbability(decimal: number): number {
  return (1 / decimal) * 100;
}

/**
 * Calculate seed distribution weight from decimal odds
 * Used for pool seeding based on betting odds
 * @param decimal Decimal odds
 * @returns Weight as integer (0-100)
 */
export function calculateSeedWeight(decimal: number): number {
  return Math.round(getImpliedProbability(decimal));
}